import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { EntityLinkSelector, STANDARD_LINKABLE_TYPES } from '@/components/shared/EntityLinkSelector';
import { UserSelector } from '@/components/shared/UserSelector';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface SupplierCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any, links?: SelectedEntity[]) => void;
  isCreating?: boolean;
  preLinkedEntity?: SelectedEntity;
}

// Exclude 'supplier' from allowed types (can't link supplier to itself)
const ALLOWED_LINK_TYPES = STANDARD_LINKABLE_TYPES.filter(t => t !== 'supplier');

export function SupplierCreateDialog({ open, onOpenChange, onSave, isCreating, preLinkedEntity }: SupplierCreateDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    criticality: 'Medium',
    notes: '',
    last_assessment: '',
    next_review: '',
    questionnaire_score: undefined as number | undefined,
    issues_count: 0,
    owner_id: undefined as string | undefined,
  });
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);

  // Pre-populate links when entity is provided
  useEffect(() => {
    if (preLinkedEntity && open) {
      setSelectedLinks([preLinkedEntity]);
    } else if (!open) {
      setSelectedLinks([]);
    }
  }, [preLinkedEntity, open]);

  const handleSubmit = () => {
    onSave(formData, selectedLinks);
    setFormData({
      name: '',
      category: '',
      criticality: 'Medium',
      notes: '',
      last_assessment: '',
      next_review: '',
      questionnaire_score: undefined,
      issues_count: 0,
      owner_id: undefined,
    });
    setSelectedLinks([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Supplier</DialogTitle>
          <DialogDescription>
            Add a third-party supplier or vendor to your organization.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Supplier Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Cloud Storage Provider Inc."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., Cloud Services"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="criticality">Criticality</Label>
              <Select
                value={formData.criticality}
                onValueChange={(value) => setFormData({ ...formData, criticality: value })}
              >
                <SelectTrigger id="criticality">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Very Low">Very Low</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Very High">Very High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Last Assessment Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.last_assessment && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.last_assessment ? format(new Date(formData.last_assessment), 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.last_assessment ? new Date(formData.last_assessment) : undefined}
                    onSelect={(date) => setFormData({ ...formData, last_assessment: date ? format(date, 'yyyy-MM-dd') : '' })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid gap-2">
              <Label>Next Review Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.next_review && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.next_review ? format(new Date(formData.next_review), 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.next_review ? new Date(formData.next_review) : undefined}
                    onSelect={(date) => setFormData({ ...formData, next_review: date ? format(date, 'yyyy-MM-dd') : '' })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="questionnaire_score">Questionnaire Score (0-100)</Label>
              <Input
                id="questionnaire_score"
                type="number"
                min="0"
                max="100"
                value={formData.questionnaire_score || ''}
                onChange={(e) => setFormData({ ...formData, questionnaire_score: e.target.value ? parseInt(e.target.value) : undefined })}
                placeholder="Optional"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="issues_count">Issues Count</Label>
              <Input
                id="issues_count"
                type="number"
                min="0"
                value={formData.issues_count || 0}
                onChange={(e) => setFormData({ ...formData, issues_count: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional information about this supplier..."
              rows={3}
            />
          </div>

          <UserSelector
            value={formData.owner_id}
            onChange={(userId) => setFormData({ ...formData, owner_id: userId })}
            label="Relationship Owner"
            placeholder="Select owner"
          />

          <EntityLinkSelector
            allowedTypes={ALLOWED_LINK_TYPES}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!formData.name || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Supplier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
